import { useCallback, useEffect, useMemo } from "react";

import immutableUpdate from "immutability-helper";
import type { UseFormSetValue } from "react-hook-form";

import { useHightouchForm } from "src/components/form";
import {
  AnalyticsFrequency,
  type AttributionWindow,
  NormalizationType,
  type VisualQueryFilter,
} from "src/types/visual";

import { ALL_DATA_MEASURING_SELECTION } from "src/pages/analytics/constants";
import {
  type AnalyticsActions,
  type ChartFormState,
  ChartTab,
  isSyntheticMetric,
  type MeasuringSelection,
  TimeOptions,
} from "src/pages/analytics/types";
import {
  graphTypeRequiresAllFrequency,
  MeasuringMode,
  type GroupByColumn,
  GraphType,
} from "@hightouch/lib/query/visual/types";
import {
  DEFAULT_GROUP_BY_COLUMNS,
  DEFAULT_METRIC_SELECTION,
  defaultExperimentsFormState,
  defaultFormState,
  defaultFunnelChartState,
  defaultInsightsChartState,
} from "./constants";
import pick from "lodash/pick";

type UseChartStateArgs = {
  useUrlState?: boolean;
  parentModelId?: number;
};

export const useChartState = ({
  useUrlState,
  parentModelId,
}: UseChartStateArgs) => {
  const form = useHightouchForm({
    // not using submit in this product.
    // instead, the polling happens when form is valid
    onSubmit: () => Promise.resolve(),
    values: { ...defaultFormState, parentModelId },
    queryKey: useUrlState ? "data" : undefined,
  });

  const setValue: UseFormSetValue<ChartFormState> = useCallback(
    (name, value) =>
      // @ts-expect-error - Circular reference problem with Column types
      form.setValue(name, value, { shouldDirty: true }),
    [form],
  );

  const selectParentModel = useCallback(
    (value: number) => {
      // The parent model has a lot of dependenies attached to it.
      // Changing the model makes much of the form state stale.

      form.reset({
        // Get current values
        ...form.getValues(),

        // Reset fields that depend on the parent model
        ...pick(defaultFormState, [
          // Shared between chart types
          "groupByColumns",
          "selectedAudiences",
          "metricSelection",

          // Funnels
          "funnelSteps",

          // Decision engine
          "measuringSelection",

          // Experiments
          "cohorts",
          "globalCohortFilter",
        ]),

        // Set new parent model
        parentModelId: value,
      });
    },
    [form],
  );

  const applySavedParentModel = useCallback(
    (value: number) => {
      // When applying the saved parent model from a saved chart, we should not reset the rest of the
      // form state.
      form.setValue("parentModelId", value);
    },
    [form],
  );

  const selectMeasurement = useCallback(
    (measuringSelection: MeasuringSelection) => {
      form.setValue("measuringSelection", measuringSelection);

      // Reset the form as certain options are dependent on the measuring scope
      setValue("metricSelection", DEFAULT_METRIC_SELECTION);
      setValue("groupByColumns", DEFAULT_GROUP_BY_COLUMNS);
      setValue("measuringMode", undefined);
    },
    [form, setValue],
  );

  /**
   * Set the measuring mode and whatever defaults are needed for that mode
   * Also reset any fields that could be dependent on the mode
   */
  const selectMeasurementMode = useCallback(
    (mode: MeasuringMode) => {
      const metricSelections = form.getValues("metricSelection");

      if (mode === MeasuringMode.Incrementality) {
        const filteredMetricSelection = metricSelections
          .filter((metric) => !isSyntheticMetric(metric))
          .map((metric) => ({
            ...metric,
            normalization: NormalizationType.TreatmentSize,
          }));

        form.reset({
          ...form.getValues(),
          measuringMode: mode,
          groupByColumns: DEFAULT_GROUP_BY_COLUMNS,
          metricSelection:
            filteredMetricSelection?.length > 0
              ? filteredMetricSelection
              : DEFAULT_METRIC_SELECTION,
          cumulative: true,
        });
      } else {
        form.reset({
          ...form.getValues(),
          measuringMode: mode,
          groupByColumns: DEFAULT_GROUP_BY_COLUMNS,
          metricSelection: metricSelections.map((metric) => ({
            ...metric,
            normalization: undefined,
          })),
          cumulative: false,
        });
      }
    },
    [form],
  );

  const addGroupByColumn = useCallback(
    (column: GroupByColumn | undefined, index: number) => {
      const groupByColumns = form.getValues("groupByColumns") ?? [];
      setValue(
        "groupByColumns",
        immutableUpdate(groupByColumns, { [index]: { $set: column } }),
      );
    },
    [form, setValue],
  );

  // TODO: combine these two functions into one
  const addGroupByColumns = useCallback(
    (columns: (GroupByColumn | undefined)[], fromIndex: number) => {
      const groupByColumns = form.getValues("groupByColumns") ?? [];

      const prevGroupBys = groupByColumns.slice(0, fromIndex);
      const groupByUpdate = prevGroupBys.concat(columns);

      setValue("groupByColumns", groupByUpdate);
    },
    [form, setValue],
  );

  const removeGroupByColumns = useCallback(
    (startIndex: number, endIndex?: number) => {
      const groupByColumns = form.getValues("groupByColumns") ?? [];
      const newGroupByColumns = groupByColumns
        .slice(0, startIndex)
        .concat(groupByColumns.slice((endIndex ?? startIndex) + 1));

      setValue(
        "groupByColumns",
        newGroupByColumns.length > 0
          ? newGroupByColumns
          : DEFAULT_GROUP_BY_COLUMNS,
      );
    },
    [form, setValue],
  );

  const resetView = useCallback(
    (chartTab: ChartTab) => {
      if (chartTab === ChartTab.Funnel) {
        form.reset({
          ...form.getValues(),
          measuringSelection: ALL_DATA_MEASURING_SELECTION,
          ...defaultFunnelChartState,
        });
      } else if (chartTab === ChartTab.SplitsMeasurement) {
        form.reset({
          ...form.getValues(),
          measuringSelection: ALL_DATA_MEASURING_SELECTION,
          ...defaultInsightsChartState,
          ...defaultExperimentsFormState,
          timeValue: TimeOptions.NinetyDays,
          graphType: GraphType.Line,
        });
      } else {
        form.reset({
          ...form.getValues(),
          measuringSelection: ALL_DATA_MEASURING_SELECTION,
          ...defaultInsightsChartState,
        });
      }
    },
    [form],
  );

  const setLookbackWindow = useCallback(
    (value: TimeOptions) => {
      setValue("selectedDates", []);
      setValue("timeValue", value);
    },
    [setValue],
  );

  const setSelectedDates = useCallback(
    (dates: Date[]) => {
      setValue(
        "selectedDates",
        dates.map((date) => date.toISOString()),
      );
      setValue("timeValue", TimeOptions.Custom);
    },
    [setValue],
  );

  const graphType = form.watch("graphType");

  // Ensure that rollupFrequency is not 'all' for certain graph types
  useEffect(() => {
    if (
      !graphTypeRequiresAllFrequency(graphType) &&
      form.getValues("rollupFrequency") === AnalyticsFrequency.All
    ) {
      setValue("rollupFrequency", AnalyticsFrequency.Weekly);
    }
  }, [graphType, form, setValue]);

  const setChartTab = useCallback(
    (tab: ChartTab) => {
      form.setValue("chartTab", tab);
      resetView(tab);
    },
    [form, resetView],
  );

  const setGlobalMeasurementWindow = useCallback(
    (measurementWindow: AttributionWindow | undefined) => {
      setValue("globalMeasurementWindow", measurementWindow);
    },
    [setValue],
  );

  const setGlobalCohortFilter = useCallback(
    (cohortFilter: VisualQueryFilter | undefined) => {
      setValue("globalCohortFilter", cohortFilter);
    },
    [setValue],
  );

  const setGlobalNormalization = useCallback(
    (normalization: NormalizationType | undefined) => {
      setValue("globalNormalization", normalization);
    },
    [setValue],
  );

  const actions: AnalyticsActions = useMemo(
    () => ({
      addGroupByColumn,
      addGroupByColumns,
      removeGroupByColumns,
      resetView,
      selectParentModel,
      applySavedParentModel,
      setLookbackWindow,
      setSelectedDates,
      selectMeasurement,
      selectMeasurementMode,
      setChartTab,
      setGlobalMeasurementWindow,
      setGlobalCohortFilter,
      setGlobalNormalization,
    }),
    [
      addGroupByColumn,
      addGroupByColumns,
      removeGroupByColumns,
      resetView,
      selectParentModel,
      applySavedParentModel,
      setLookbackWindow,
      setSelectedDates,
      selectMeasurement,
      selectMeasurementMode,
      setChartTab,
      setGlobalMeasurementWindow,
      setGlobalCohortFilter,
      setGlobalNormalization,
    ],
  );

  return {
    form,
    ...actions,
  };
};
